import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { MapPin, Briefcase, ArrowUpRight, X } from "lucide-react";
import SEO from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Careers() {
  const [openings, setOpenings] = useState([]);
  const [filter, setFilter] = useState("All");
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    location: "",
    years_experience: "",
    linkedin: "",
    cover_letter: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/careers/openings`)
      .then((res) => setOpenings(res.data.openings || []))
      .catch(() => setOpenings([]));
  }, []);

  const categories = ["All", ...Array.from(new Set(openings.map((o) => o.category)))];
  const filtered = filter === "All" ? openings : openings.filter((o) => o.category === filter);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !selected) {
      toast.error("Fill name, email and select a role");
      return;
    }
    try {
      setSubmitting(true);
      await axios.post(`${API}/careers/apply`, {
        ...form,
        role: selected.title,
        years_experience: form.years_experience ? parseInt(form.years_experience) : null,
      });
      toast.success("Application received. Our talent team will reach out.");
      setSelected(null);
      setForm({
        full_name: "",
        email: "",
        phone: "",
        location: "",
        years_experience: "",
        linkedin: "",
        cover_letter: "",
      });
    } catch {
      toast.error("Could not submit. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="careers-page">
      <SEO
        title="Careers at Notion Minds Systems — Technology Opportunities in Saudi Arabia"
        description="Join Notion Minds Systems — technology and consulting opportunities in Saudi Arabia, including independent business development partnerships."
        canonical="https://notionminds.sa/careers"
      />
      <section className="pt-24 pb-12 max-w-[1440px] mx-auto px-6 lg:px-12">
        <p className="font-mono-accent text-xs text-[#d4af37] mb-5">[ Careers ]</p>
        <h1 className="font-display text-5xl lg:text-7xl font-light leading-[1.05] max-w-4xl">
          Build the region's most <span className="text-gradient-gold font-semibold">deliberate consulting practice.</span>
        </h1>
        <p className="mt-8 text-white/65 text-lg max-w-2xl">
          We hire people who think like operators and ship like engineers. If you
          want consequential work alongside senior practitioners, this is the room.
        </p>
      </section>

      <section className="max-w-[1440px] mx-auto px-6 lg:px-12 pb-24">
        <div className="flex flex-wrap gap-2 mb-10">
          {categories.map((c) => (
            <button
              key={c}
              data-testid={`career-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setFilter(c)}
              className={`px-4 py-2 text-xs font-mono-accent border transition-colors ${
                filter === c
                  ? "border-[#d4af37] text-[#d4af37] bg-[#d4af37]/5"
                  : "border-white/15 text-white/60 hover:border-white/40"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="space-y-px bg-[#1a1a20]">
          {filtered.length === 0 && (
            <div
              data-testid="no-openings-message"
              className="bg-[#050505] p-12 lg:p-16 text-center"
            >
              <p className="font-mono-accent text-xs text-[#d4af37] mb-4">
                Current openings
              </p>
              <h3 className="font-display text-2xl lg:text-3xl font-light mb-4">
                No open roles right now.
              </h3>
              <p className="text-white/55 text-sm max-w-md mx-auto leading-relaxed">
                We're a growing practice and hire on a rolling basis. Send your
                profile to{" "}
                <a
                  href="mailto:careers@notionminds.sa"
                  className="text-[#d4af37] hover:underline"
                  data-testid="careers-general-email"
                >
                  careers@notionminds.sa
                </a>{" "}
                and we'll reach out when a matching role opens.
              </p>
            </div>
          )}
          {filtered.map((o, i) => {
            const cls =
              "w-full group bg-[#050505] hover:bg-[#0e0e14] p-8 lg:p-10 text-left transition-colors flex flex-col md:flex-row md:items-center justify-between gap-4";
            const inner = (
              <>
                <div className="flex-1">
                  <p className="font-mono-accent text-xs text-[#d4af37] mb-3">{o.category}</p>
                  <h3 className="font-display text-2xl lg:text-3xl font-light group-hover:text-[#d4af37] transition-colors">
                    {o.title}
                  </h3>
                  <div className="flex flex-wrap gap-5 mt-4 text-sm text-white/50">
                    <span className="inline-flex items-center gap-2"><MapPin className="w-4 h-4" /> {o.location}</span>
                    <span className="inline-flex items-center gap-2"><Briefcase className="w-4 h-4" /> {o.type}</span>
                  </div>
                </div>
                <div className="inline-flex items-center gap-2 text-[#d4af37] text-sm font-mono-accent">
                  View role <ArrowUpRight className="w-4 h-4" />
                </div>
              </>
            );
            if (o.id === "bd-partner-ksa-001") {
              return (
                <motion.div
                  key={o.id}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                >
                  <Link
                    to="/careers/business-development-partner-saudi-arabia"
                    data-testid={`opening-${o.id}`}
                    className={cls}
                  >
                    {inner}
                  </Link>
                </motion.div>
              );
            }
            return (
              <motion.button
                key={o.id}
                data-testid={`opening-${o.id}`}
                onClick={() => setSelected(o)}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={cls}
              >
                {inner}
              </motion.button>
            );
          })}
        </div>
      </section>

      {selected && (
        <div
          data-testid="application-modal"
          className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-6"
          onClick={() => setSelected(null)}
        >
          <div
            className="w-full max-w-3xl bg-[#0a0a0e] border border-[#24242a] max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between p-8 border-b border-white/10 sticky top-0 bg-[#0a0a0e] z-10">
              <div>
                <p className="font-mono-accent text-xs text-[#d4af37] mb-2">{selected.category}</p>
                <h3 className="font-display text-2xl font-light">{selected.title}</h3>
                <p className="text-sm text-white/50 mt-1">{selected.location} · {selected.type}</p>
              </div>
              <button
                onClick={() => setSelected(null)}
                data-testid="close-application-modal"
                className="text-white/60 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {selected.details && (
              <div className="p-8 space-y-8" data-testid="opening-details">
                {selected.details.map((sec, idx) => (
                  <section key={idx} data-testid={`opening-section-${idx}`}>
                    {sec.heading && (
                      <h4 className="font-display text-xl font-light mb-4 text-[#d4af37]">
                        {sec.heading}
                      </h4>
                    )}
                    {sec.paragraphs?.map((p, pi) => (
                      <p key={pi} className="text-white/70 text-sm leading-relaxed mb-4">
                        {p}
                      </p>
                    ))}
                    {sec.chips && (
                      <div className="flex flex-wrap gap-2 my-4">
                        {sec.chips.map((c) => (
                          <span
                            key={c}
                            className="text-xs font-mono-accent text-[#d4af37] border border-[#d4af37]/40 px-3 py-1.5"
                          >
                            {c}
                          </span>
                        ))}
                      </div>
                    )}
                    {sec.after && (
                      <p className="text-white/70 text-sm leading-relaxed mt-4">{sec.after}</p>
                    )}
                    {sec.groups && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 my-4">
                        {sec.groups.map((g) => (
                          <div key={g.name} className="p-4 bg-[#0d0d12] border border-[#1c1c22]">
                            <p className="font-display text-sm font-medium text-white mb-1.5">{g.name}</p>
                            <p className="text-xs text-white/55 leading-relaxed">{g.items}</p>
                          </div>
                        ))}
                      </div>
                    )}
                    {sec.bullets && (
                      <ul className="space-y-2.5 my-4">
                        {sec.bullets.map((b) => (
                          <li key={b} className="flex items-start gap-3 text-sm text-white/70 leading-relaxed">
                            <span className="w-1.5 h-1.5 bg-[#d4af37] mt-2 shrink-0" />
                            {b}
                          </li>
                        ))}
                      </ul>
                    )}
                  </section>
                ))}
              </div>
            )}

            {selected.apply_email && (
              <div className="px-8 pb-8" data-testid="apply-via-email-block">
                <div className="border border-[#d4af37]/40 bg-[#d4af37]/5 p-6">
                  <p className="font-mono-accent text-[10px] text-[#d4af37] mb-3">Apply now</p>
                  <p className="text-white/70 text-sm mb-5">
                    Location: {selected.location} · Engagement: {selected.type}
                  </p>
                  <a
                    href={`mailto:${selected.apply_email}?subject=${encodeURIComponent(
                      "Application: " + selected.title
                    )}`}
                    data-testid="apply-email-cta"
                    className="inline-flex items-center gap-2 bg-[#d4af37] text-black px-6 py-3.5 font-semibold hover:bg-[#e5c059] transition-colors"
                  >
                    Apply at {selected.apply_email} <ArrowUpRight className="w-4 h-4" />
                  </a>
                </div>
              </div>
            )}

            <div className="px-8 pb-6">
              <p className="font-mono-accent text-[10px] text-white/40 mb-3 mt-2">
                Or apply through this form
              </p>
            </div>

            <form onSubmit={submit} className="p-8 pt-0 space-y-4">
              <Field
                label="Full name *"
                testid="apply-name"
                value={form.full_name}
                onChange={(v) => setForm({ ...form, full_name: v })}
              />
              <Field
                label="Email *"
                testid="apply-email"
                type="email"
                value={form.email}
                onChange={(v) => setForm({ ...form, email: v })}
              />
              <div className="grid grid-cols-2 gap-4">
                <Field
                  label="Phone"
                  testid="apply-phone"
                  value={form.phone}
                  onChange={(v) => setForm({ ...form, phone: v })}
                />
                <Field
                  label="Location"
                  testid="apply-location"
                  value={form.location}
                  onChange={(v) => setForm({ ...form, location: v })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Field
                  label="Years of experience"
                  testid="apply-years"
                  type="number"
                  value={form.years_experience}
                  onChange={(v) => setForm({ ...form, years_experience: v })}
                />
                <Field
                  label="LinkedIn URL"
                  testid="apply-linkedin"
                  value={form.linkedin}
                  onChange={(v) => setForm({ ...form, linkedin: v })}
                />
              </div>
              <div>
                <label className="font-mono-accent text-[10px] text-white/50 mb-2 block">
                  Cover letter
                </label>
                <textarea
                  data-testid="apply-cover-letter"
                  rows={5}
                  value={form.cover_letter}
                  onChange={(e) => setForm({ ...form, cover_letter: e.target.value })}
                  className="w-full bg-transparent border border-white/15 focus:border-[#d4af37] px-4 py-3 text-sm outline-none resize-none transition-colors"
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid="submit-application"
                className="w-full bg-[#d4af37] text-black font-semibold py-3.5 hover:bg-[#e5c059] transition-colors disabled:opacity-60"
              >
                {submitting ? "Submitting..." : "Submit application"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, testid, type = "text" }) {
  return (
    <div>
      <label className="font-mono-accent text-[10px] text-white/50 mb-2 block">{label}</label>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-transparent border border-white/15 focus:border-[#d4af37] px-4 py-3 text-sm outline-none transition-colors"
      />
    </div>
  );
}
